import { useEffect, useMemo, useState } from "react";
import { api, fmtErr, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Info, CheckCircle } from "lucide-react";

const STATUS_LABELS = { envoye: "ENVOYÉ", simule_envoye: "SIMULÉ", erreur: "ERREUR" };
const STATUS_STYLES = {
  envoye: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300",
  simule_envoye: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  erreur: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
};

const DELIVERY_LABELS = {
  accepted: "ACCEPTÉ", requested: "DEMANDÉ", sent: "ENVOYÉ", delivered: "DÉLIVRÉ",
  deferred: "DIFFÉRÉ", soft_bounce: "REBOND SOFT", hard_bounce: "REBOND HARD",
  blocked: "BLOQUÉ", spam: "SPAM", invalid_email: "EMAIL INVALIDE",
  unsubscribed: "DÉSABONNÉ", error: "ERREUR",
};
const DELIVERY_STYLES = {
  delivered: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300",
  accepted: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  requested: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  sent: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  deferred: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  soft_bounce: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  hard_bounce: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
  blocked: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
  spam: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
  invalid_email: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
  error: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
  unsubscribed: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
};

function StatusBadge({ status }) {
  return (
    <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.simule_envoye}`} data-testid={`email-status-${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function DeliveryBadge({ status, reason }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span title={reason || ""} data-testid={`email-delivery-${status}`}
      className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold ${DELIVERY_STYLES[status] || DELIVERY_STYLES.accepted}`}>
      {DELIVERY_LABELS[status] || status}
    </span>
  );
}

export default function EmailLogsPage() {
  const [logs, setLogs] = useState(null);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [fTemplate, setFTemplate] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fMode, setFMode] = useState("all");
  const [fDelivery, setFDelivery] = useState("all");
  const [visible, setVisible] = useState(100);

  useEffect(() => {
    api.get("/email-logs").then(({ data }) => setLogs(data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/config/public").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const openEmail = async (id) => {
    try {
      const { data } = await api.get(`/email-logs/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const templates = useMemo(() => [...new Set((logs || []).map((l) => l.template))].sort(), [logs]);
  const filtered = useMemo(() => (logs || []).filter((l) =>
    (fTemplate === "all" || l.template === fTemplate) &&
    (fStatus === "all" || l.status === fStatus) &&
    (fMode === "all" || (l.email_mode || "simulated") === fMode) &&
    (fDelivery === "all" || (l.delivery_status || "") === fDelivery)
  ), [logs, fTemplate, fStatus, fMode, fDelivery]);
  const shown = filtered.slice(0, visible);
  const realMode = config?.email_mode === "real";

  return (
    <div data-testid="email-logs-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Emails & notifications</h1>
        <p className="mt-1 text-sm text-muted-foreground">Les emails sont des notifications uniquement — le traitement se fait dans Suivio360.</p>
      </div>

      {realMode ? (
        <div className="mb-6 flex items-start gap-3 rounded-sm border border-green-300 bg-green-50 p-4 text-sm text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200" data-testid="email-real-banner">
          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Envois réels activés (Brevo) — environnement {config.environment}.</b> Les emails sont réellement envoyés
            depuis <b>{(logs || []).find((l) => l.email_mode === "real")?.from_email || "l'expéditeur configuré"}</b>.
            Chaque envoi est tracé ici avec son identifiant Brevo.
          </div>
        </div>
      ) : (
        <div className="mb-6 flex items-start gap-3 rounded-sm border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200" data-testid="email-simulation-banner">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Mode simulation.</b> Aucun email réel n'est envoyé. Chaque notification est enregistrée ici avec son contenu complet.
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={fTemplate} onValueChange={setFTemplate}>
          <SelectTrigger className="w-56 rounded-sm" data-testid="email-filter-template"><SelectValue placeholder="Template" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les templates</SelectItem>
            {templates.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="email-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="envoye">Envoyé</SelectItem>
            <SelectItem value="simule_envoye">Simulé</SelectItem>
            <SelectItem value="erreur">Erreur</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fMode} onValueChange={setFMode}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="email-filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les modes</SelectItem>
            <SelectItem value="real">Réel</SelectItem>
            <SelectItem value="simulated">Simulé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fDelivery} onValueChange={setFDelivery}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="email-filter-delivery"><SelectValue placeholder="Délivrance" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toute délivrance</SelectItem>
            <SelectItem value="delivered">Délivré</SelectItem>
            <SelectItem value="soft_bounce">Rebond soft</SelectItem>
            <SelectItem value="hard_bounce">Rebond hard</SelectItem>
            <SelectItem value="blocked">Bloqué</SelectItem>
            <SelectItem value="spam">Spam</SelectItem>
            <SelectItem value="invalid_email">Email invalide</SelectItem>
            <SelectItem value="deferred">Différé</SelectItem>
            <SelectItem value="error">Erreur</SelectItem>
            <SelectItem value="accepted">Accepté (en attente)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground" data-testid="email-filter-count">{filtered.length} email(s)</span>
      </div>

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        {logs === null ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="email-logs-loading">Chargement…</div>
        ) : (
        <table className="w-full text-sm" data-testid="email-logs-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3 font-semibold">Date</th>
              <th className="px-5 py-3 font-semibold">Template</th>
              <th className="px-5 py-3 font-semibold">Destinataire</th>
              <th className="px-5 py-3 font-semibold">Sujet</th>
              <th className="px-5 py-3 font-semibold">Société</th>
              <th className="px-5 py-3 font-semibold">Statut</th>
              <th className="px-5 py-3 font-semibold">Délivrance</th>
              <th className="px-5 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.id} className="border-b border-border last:border-b-0 hover:bg-accent transition-colors duration-200" data-testid={`email-row-${l.id}`}>
                <td className="whitespace-nowrap px-5 py-3 text-xs text-muted-foreground">{fmtDateTime(l.created_at)}</td>
                <td className="px-5 py-3"><code className="text-xs font-semibold">{l.template}</code></td>
                <td className="px-5 py-3">
                  <div>{l.recipient_name}</div>
                  <div className="text-xs text-muted-foreground">{l.recipient_email}</div>
                </td>
                <td className="max-w-64 truncate px-5 py-3">{l.subject}</td>
                <td className="px-5 py-3 text-muted-foreground">{l.company_name}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={l.status} />
                    {l.email_mode === "real" && l.status === "envoye" && <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">RÉEL</span>}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <DeliveryBadge status={l.delivery_status} reason={l.brevo_reason} />
                  {l.brevo_reason && <div className="mt-0.5 max-w-48 truncate text-[10px] text-muted-foreground" title={l.brevo_reason}>{l.brevo_reason}</div>}
                </td>
                <td className="px-5 py-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`view-email-${l.id}`} onClick={() => openEmail(l.id)} className="rounded-sm">
                    <Eye className="mr-1.5 h-3.5 w-3.5" /> Voir
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
        {logs && filtered.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground" data-testid="email-logs-empty">Aucun email pour ces filtres.</div>}
        {filtered.length > visible && (
          <div className="border-t border-border p-3 text-center">
            <Button size="sm" variant="outline" className="rounded-sm" data-testid="email-load-more" onClick={() => setVisible((v) => v + 100)}>
              Afficher plus ({filtered.length - visible} restants)
            </Button>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle>Aperçu de l'email</DialogTitle>
            <DialogDescription>
              {selected && <>De {selected.from_name} &lt;{selected.from_email}&gt; · Reply-To : {selected.reply_to}</>}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div>
              <div className="mb-4 grid grid-cols-2 gap-3 rounded-sm border border-border p-4 text-xs">
                <div><span className="text-muted-foreground">Template :</span> <code className="font-semibold">{selected.template}</code></div>
                <div className="flex items-center gap-2"><span className="text-muted-foreground">Statut :</span> <StatusBadge status={selected.status} /></div>
                <div><span className="text-muted-foreground">Mode :</span> {selected.email_mode === "real" ? "Envoi réel (Brevo)" : "Simulation"}</div>
                <div><span className="text-muted-foreground">Brevo :</span> {selected.brevo_status ? `HTTP ${selected.brevo_status}` : "—"}</div>
                <div className="flex items-center gap-2"><span className="text-muted-foreground">Délivrance :</span> <DeliveryBadge status={selected.delivery_status} reason={selected.brevo_reason} /></div>
                {selected.last_brevo_event && (
                  <div><span className="text-muted-foreground">Dernier événement Brevo :</span> <code>{selected.last_brevo_event}</code></div>
                )}
                {selected.brevo_reason && (
                  <div className="col-span-2 break-all" data-testid="email-brevo-reason"><span className="text-muted-foreground">Motif Brevo :</span> {selected.brevo_reason}</div>
                )}
                <div><span className="text-muted-foreground">Destinataire :</span> {selected.recipient_email}</div>
                <div><span className="text-muted-foreground">Date :</span> {fmtDateTime(selected.created_at)}</div>
                {selected.brevo_message_id && (
                  <div className="col-span-2 break-all" data-testid="email-brevo-message-id"><span className="text-muted-foreground">Message ID Brevo :</span> <code>{selected.brevo_message_id}</code></div>
                )}
                {selected.error && (
                  <div className="col-span-2 break-all text-red-600" data-testid="email-error"><span className="text-muted-foreground">Erreur :</span> {selected.error}</div>
                )}
                <div className="col-span-2"><span className="text-muted-foreground">Sujet :</span> {selected.subject}</div>
              </div>
              <div className="overflow-hidden rounded-sm border border-border" dangerouslySetInnerHTML={{ __html: selected.content_html }} data-testid="email-html-preview" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
